import { useState, useEffect, useRef } from "react";
import { products, actionPhotos } from "@/mocks/products";

const PHG_LOGO = "/images/pwg1.png";
const CDBJJ_LOGO = "/images/carpe2.png";

export default function ProductDetailSections() {
  return (
    <section className="w-full bg-black">
      {products.map((product, i) => (
        <ProductDetailBlock key={product.id} product={product} index={i} />
      ))}
    </section>
  );
}

function useReveal(threshold = 0.06) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

// 各商品のモデルカット割り当て（着用アイテムに合わせて厳密に管理）
// L/S: [14]=_A1_5337長袖BLACK, [13]=_A1_5357長袖GRAY
// H/S: [5]=_A1_5561半袖BLACK,  [6]=_A1_5580半袖GRAY
// Fight Shorts: [9]=_A1_5559ショーツBLACK, [10]=_A1_5592ショーツGRAY
// Tee: [0]=_A1_5320 TEE集合, [1]=_A1_5314 TEE全員
const productHeroPhotos: Record<string, { black: number; gray: number; single: number }> = {
  "rush-ls":      { black: 14, gray: 15, single: 14 }, // 長袖のみ
  "rush-ss":      { black: 17, gray: 16, single: 17 }, // 半袖のみ
  "fight-shorts": { black: 20, gray: 21, single: 20 }, // ショーツのみ
  "tee":          { black: 18, gray: 18, single: 18 }, // Tシャツのみ
};

// 詳細ブロック補助画像（物撮り — 各商品に対応したもの）
const productSubPhotos: Record<string, { black: string; gray: string; single: string }> = {
  // L/S: 長袖ラッシュガード物撮り
  "rush-ls": {
    // L/S BLACK物撮り (__3.jpg)
    black: "/images/3.jpg",
    // L/S GRAY物撮り (高級ライティング_8_2)
    gray:  "/images/8_2.jpg",
    single: "/images/3.jpg",
  },
  // H/S: 半袖ラッシュガード物撮り
  "rush-ss": {
    // H/S BLACK物撮り (高級感のある物撮りライティング_8)
    black: "/images/8.jpg",
    // H/S GRAY物撮り (高級感のある物撮りライティング_1)
    gray:  "/images/1.jpg",
    single: "/images/8.jpg",
  },
  // Fight Shorts: ショーツ物撮り
  "fight-shorts": {
    black: "/images/5.jpg",
    gray:  "/images/10.png",
    single: "/images/5.jpg",
  },
  // Tee: Tシャツ物撮り
  "tee": {
    black: "/images/A1_5559.jpg",
    gray:  "/images/A1_5559.jpg",
    single: "/images/A1_5559.jpg",
  },
};

function ProductDetailBlock({
  product,
  index,
}: {
  product: (typeof products)[0];
  index: number;
}) {
  const [activeTab, setActiveTab] = useState<"desc" | "size">("desc");
  const hasColors = product.colors && product.colors.length > 0;
  const { ref: heroRef, visible: heroVisible } = useReveal(0.04);
  const { ref: infoRef, visible: infoVisible } = useReveal(0.06);

  const isEven = index % 2 === 0;
  const heroMap = productHeroPhotos[product.slug];
  const subMap  = productSubPhotos[product.slug];

  return (
    <div id={`product-${product.slug}`} className="w-full">

      {/* ══════════════════════════════════════
          HERO VISUAL — フルスクリーン モデルカット
      ══════════════════════════════════════ */}
      <div
        ref={heroRef}
        className={`relative w-full overflow-hidden transition-opacity duration-1000 ${heroVisible ? "opacity-100" : "opacity-0"}`}
        style={{ minHeight: "100vh" }}
      >
        {hasColors ? (
          /* 2色: 左右フルスクリーン分割 */
          <div className="flex w-full" style={{ minHeight: "100vh" }}>
            {product.colors!.map((color, ci) => {
              const heroSrc = actionPhotos[(ci === 0 ? heroMap?.black : heroMap?.gray) ?? 0];
              const isGray = ci === 1;
              const isFightShorts = product.slug === "fight-shorts";
              return (
                <div key={color.label} className="relative flex-1 overflow-hidden" style={{ minHeight: "100vh" }}>
                  <img
                    src={heroSrc}
                    alt={`${product.name} ${color.label}`}
                    className={`w-full h-full object-cover transition-transform duration-[1.4s] ${heroVisible ? "scale-100" : "scale-110"} ${isGray && isFightShorts ? "object-bottom" : "object-center"}`}
                    style={{ minHeight: "100vh" }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/35" />

                  <div className="absolute bottom-12 left-0 right-0 flex flex-col items-center gap-2">
                    <span className="text-white/80 tracking-[0.3em] uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "13px" }}>
                      {color.label}
                    </span>
                    <div className="w-8 h-px bg-white/40" />
                  </div>

                  {ci === 0 && <div className="absolute top-0 right-0 w-px h-full bg-white/10 z-10" />}
                </div>
              );
            })}

            {/* Center: big number */}
            <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
              <div
                className={`text-white/5 font-black select-none transition-all duration-1000 delay-300 ${heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(120px, 20vw, 260px)", lineHeight: 0.85 }}
              >
                {String(index + 1).padStart(2, "0")}
              </div>
            </div>

            {/* Top-left: name + カテゴリ */}
            <div className={`absolute top-6 left-4 md:top-10 md:left-10 z-30 transition-all duration-700 delay-200 ${heroVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
              <p className="text-red-500 text-[10px] tracking-[0.3em] uppercase mb-1 md:mb-2">{product.category}</p>
              <h2 className="text-white font-black uppercase leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(16px, 3.5vw, 52px)", letterSpacing: "0.04em" }}>
                {product.name}
              </h2>
            </div>

            {/* Top-right: price */}
            <div className={`absolute top-6 right-4 md:top-10 md:right-10 z-30 text-right transition-all duration-700 delay-300 ${heroVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
              <p className="text-white/30 text-[10px] tracking-widest uppercase mb-1">PRICE</p>
              <p className="text-white font-black" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(14px, 2.5vw, 36px)" }}>
                ¥{Math.floor(product.price * 1.1).toLocaleString()}
                <span className="text-white/30 text-xs ml-1 font-normal">税込</span>
              </p>
            </div>

            {/* Bottom: 2 COLORS + scroll cue */}
            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-3">
              <span className="text-white/40 text-[10px] tracking-[0.4em] uppercase border border-white/20 px-4 py-1">2 COLORS</span>
              <div className="w-px h-8 bg-white/20" />
              <i className="ri-arrow-down-line text-white/30 text-sm" />
            </div>
          </div>
        ) : (
          /* TEE: フルスクリーン */
          <div className="relative w-full" style={{ minHeight: "100vh" }}>
            <img
              src={actionPhotos[heroMap?.single ?? 0]}
              alt={product.name}
              className={`w-full h-full object-cover object-center transition-transform duration-[1.4s] ${heroVisible ? "scale-100" : "scale-110"}`}
              style={{ minHeight: "100vh", position: "absolute", inset: 0 }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/30 to-black/10" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />



            <div className={`absolute inset-0 flex flex-col justify-end pb-16 md:justify-end md:pb-20 pl-6 md:pl-20 transition-all duration-700 delay-200 ${heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <p className="text-red-500 text-[10px] tracking-[0.4em] uppercase mb-3 md:mb-4">{product.category}</p>
              <h2 className="text-white font-black uppercase leading-none mb-3 md:mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(36px, 7vw, 100px)", letterSpacing: "0.02em" }}>
                {product.name}
              </h2>
              <p className="text-white/50 text-sm max-w-xs leading-relaxed mb-4 md:mb-6">{product.tagline}</p>
              <div className="flex items-center gap-4">
                <span className="text-white font-black" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(22px, 4vw, 32px)" }}>
                  ¥{Math.floor(product.price * 1.1).toLocaleString()}
                </span>
                <span className="text-white/30 text-xs">税込</span>
              </div>
            </div>

            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
              <div className="w-px h-8 bg-white/20" />
              <i className="ri-arrow-down-line text-white/30 text-sm" />
            </div>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════
          DETAIL INFO BLOCK
      ══════════════════════════════════════ */}
      <div
        ref={infoRef}
        className={`w-full bg-black transition-all duration-700 ${infoVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
      >
        <div className={`max-w-6xl mx-auto flex flex-col ${isEven ? "lg:flex-row" : "lg:flex-row-reverse"} gap-0`}>

          {/* 写真エリア */}
          <div className="w-full lg:w-[42%] flex flex-col gap-px bg-[#111]">
            {hasColors ? (
              <div className="flex flex-row gap-px flex-1">
                {product.colors!.map((color, ci) => {
                  const modelSrc = actionPhotos[(ci === 0 ? heroMap?.black : heroMap?.gray) ?? 0];
                  const isGrayDetail = ci === 1;
                  const isFightShortsDetail = product.slug === "fight-shorts";
                  return (
                    <div key={color.label} className="relative flex-1 flex flex-col">
                      <img
                        src={modelSrc}
                        alt={`${product.name} ${color.label}`}
                        className={`w-full h-auto block ${isGrayDetail && isFightShortsDetail ? "object-bottom" : ""}`}
                        style={{ display: "block" }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
                      <div className="absolute bottom-3 left-3">
                        <span className="text-white/50 text-[10px] tracking-[0.3em] uppercase">{color.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="relative flex-1">
                <img
                  src={actionPhotos[heroMap?.single ?? 0]}
                  alt={product.name}
                  className="w-full h-auto block"
                  style={{ display: "block" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
              </div>
            )}

            {/* 物撮りサムネイル */}
            <div className="flex flex-row gap-px">
              {hasColors ? (
                product.colors!.map((color, ci) => {
                  const subSrc = ci === 0 ? subMap?.black : subMap?.gray;
                  return (
                    <div key={color.label} className="relative flex-1 bg-[#0d0d0d]">
                      <img src={subSrc} alt={`${product.name} ${color.label}`} className="w-full h-auto block" />
                      <div className="absolute inset-0 bg-black/30 pointer-events-none" />
                      <div className="absolute bottom-2 left-2">
                        <span className="text-white/30 text-[9px] tracking-widest uppercase">{color.label}</span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="relative flex-1 bg-[#0d0d0d]">
                  <img src={subMap?.single} alt={`${product.name}`} className="w-full h-auto block" />
                  <div className="absolute inset-0 bg-black/30 pointer-events-none" />
                </div>
              )}
            </div>
          </div>

          {/* テキストエリア */}
          <div className="w-full lg:flex-1 flex flex-col justify-center px-5 md:px-16 py-10 md:py-16">
            <div className="flex items-center gap-3 mb-6 md:mb-8 flex-wrap">
              <span className="text-red-500 text-xs tracking-wider uppercase border border-red-500/60 px-3 py-1">{product.category}</span>
              {hasColors && <span className="text-white/60 text-xs tracking-wider uppercase border border-white/20 px-3 py-1">2 COLORS</span>}
            </div>

            <h3 className="text-white font-black uppercase mb-3 leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(32px, 3.5vw, 52px)", letterSpacing: "0.04em" }}>
              {product.name}
            </h3>
            <p className="text-red-400 text-base mb-3 leading-relaxed">{product.tagline}</p>
            <p className="text-white/60 text-sm tracking-wider mb-8">素材: {product.material}</p>

            {/* タブ */}
            <div className="flex gap-0 mb-6 border-b border-white/15">
              {(["desc", "size"] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`text-sm tracking-wider uppercase px-5 py-3 transition-colors duration-200 whitespace-nowrap cursor-pointer ${activeTab === tab ? "text-white border-b-2 border-red-600" : "text-white/50 hover:text-white/80"}`}
                >
                  {tab === "desc" ? "商品説明" : "サイズ表"}
                </button>
              ))}
            </div>

            {activeTab === "desc" && (
              <div className="text-white/75 text-sm leading-loose whitespace-pre-line max-h-44 overflow-y-auto pr-2">
                {product.description}
              </div>
            )}

            {activeTab === "size" && (
              <div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-white/75">
                    <thead>
                      <tr className="border-b border-white/15">
                        {product.sizeHeaders.map((h) => (
                          <th key={h} className="text-left py-2 pr-4 text-white/55 tracking-wider whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {product.sizeRows.map((row) => (
                        <tr key={row[0]} className="border-b border-white/8">
                          {row.map((cell, ci) => (
                            <td key={ci} className={`py-3 pr-4 whitespace-nowrap ${ci === 0 ? "text-white font-bold" : ""}`}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-white/50 text-xs mt-4 leading-relaxed">※ {product.sizeNote}</p>
              </div>
            )}

            <div className="flex flex-wrap gap-2 mt-6">
              {product.features?.map((f) => (
                <span key={f} className="text-xs text-white/60 border border-white/20 px-3 py-1 tracking-wide">{f}</span>
              ))}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-5 pt-8 border-t border-white/15">
              <div>
                <span className="text-white font-black text-3xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                  ¥{Math.floor(product.price * 1.1).toLocaleString()}
                </span>
                <span className="text-white/50 text-sm ml-2">税込</span>
              </div>
              <a
                href={product.shopUrl ?? `#product-${product.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm tracking-wider uppercase px-7 py-3 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                購入する <i className="ri-arrow-right-line" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* SEPARATOR */}
      {index < products.length - 1 && (
        <TextSeparator index={index} />
      )}
    </div>
  );
}

function TextSeparator({ index }: { index: number }) {
  const { ref, visible } = useReveal(0.3);

  return (
    <div
      ref={ref}
      className={`relative w-full bg-black overflow-hidden transition-all duration-700 ${visible ? "opacity-100" : "opacity-0"}`}
      style={{ height: "120px" }}
    >
      {/* マーキーテキスト */}
      <div className="absolute inset-0 flex items-center overflow-hidden pointer-events-none">
        <div className="flex gap-16 animate-marquee whitespace-nowrap">
          {Array.from({ length: 12 }).map((_, i) => (
            <span key={i} className="text-white/5 font-black uppercase select-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(50px, 7vw, 90px)", letterSpacing: "0.05em" }}>
              LIVE THE DISCIPLINE &nbsp;·&nbsp; PHG × CDBJJ &nbsp;·&nbsp;
            </span>
          ))}
        </div>
      </div>

      {/* 中央: ロゴ + ライン */}
      <div className={`absolute inset-0 flex items-center justify-center transition-all duration-700 delay-200 ${visible ? "opacity-100" : "opacity-0"}`}>
        <div className="flex items-center gap-5">
          <div className="w-12 h-px bg-white/15" />
          <div className="flex items-center gap-3 bg-black/80 px-4 py-2 border border-white/10">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <img src={PHG_LOGO} alt="PHG" className="w-full h-full object-contain opacity-60" />
            </div>
            <span className="text-white/20 text-xs" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.3em" }}>×</span>
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <img src={CDBJJ_LOGO} alt="CDBJJ" className="w-full h-full object-contain opacity-60" />
            </div>
            <span className="text-white/25 text-[9px] tracking-[0.4em] uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              {String(index + 2).padStart(2, "0")}
            </span>
          </div>
          <div className="w-12 h-px bg-white/15" />
        </div>
      </div>
    </div>
  );
}
